import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { formatDateTime } from '@/lib/i18n';
import { apiError, type ForecastHoliday } from './lib';

/**
 * §23's tenant-supplied holiday calendar. Building a day-of-week profile
 * EXCLUDES a flagged date from the training samples, so a Ramadan closure
 * (or any other date this tenant knows in advance is not an ordinary
 * Tuesday) does not drag the "every Tuesday" average down.
 */
export default function HolidaysPanel({ branchId }: { branchId: string }) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const canManage = can('forecasting.manage');

  const { data, loading, error, reload } = useApi<unknown>('/forecasting/holidays', { branchId });
  const { rows } = rowsOf<ForecastHoliday>(data);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const remove = async (holiday: ForecastHoliday) => {
    const ok = await confirm({
      title: t('forecasting.holidays.deleteTitle'),
      body: t('forecasting.holidays.deleteBody', { label: holiday.label }),
      tone: 'danger',
      confirmLabel: t('common.delete'),
    });
    if (!ok) return;
    setBusyId(holiday.id);
    try {
      await api.delete(`/forecasting/holidays/${holiday.id}`);
      toast.success(t('forecasting.holidays.deleted'));
      reload();
    } catch (e) {
      toast.error(apiError(e, t('forecasting.holidays.deleteFailed')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3">
        <div>
          <CardTitle>{t('forecasting.holidays.title')}</CardTitle>
          <p className="text-sm text-ink-muted">{t('forecasting.holidays.subtitle')}</p>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setOpen(true)}>
            <Icon as={Plus} size="sm" className="me-1.5" />
            {t('forecasting.holidays.add')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading && !data ? (
          <LoadingState label={t('common.loading')} />
        ) : error ? (
          <ErrorState message={t('forecasting.holidays.loadFailed')} onRetry={reload} />
        ) : rows.length === 0 ? (
          <EmptyState title={t('forecasting.holidays.empty')} hint={t('forecasting.holidays.emptyHint')} />
        ) : (
          <ul className="divide-y">
            {rows.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-3 py-2.5">
                <div>
                  <p className="text-sm font-medium">{h.label}</p>
                  <p className="text-xs text-ink-muted">
                    {formatDateTime(h.date, i18n.language)}{' '}
                    <Badge tone={h.branchId ? 'neutral' : 'info'} className="ms-1">
                      {h.branchId
                        ? t('forecasting.holidays.thisBranch')
                        : t('forecasting.holidays.tenantWide')}
                    </Badge>
                  </p>
                </div>
                {canManage && (
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={busyId === h.id}
                    onClick={() => void remove(h)}
                    aria-label={t('common.delete')}
                  >
                    <Icon as={Trash2} size="sm" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {open && (
        <HolidayFormDialog
          branchId={branchId}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            reload();
          }}
        />
      )}
    </Card>
  );
}

function HolidayFormDialog({
  branchId,
  onClose,
  onSaved,
}: {
  branchId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [date, setDate] = useState('');
  const [label, setLabel] = useState('');
  const [tenantWide, setTenantWide] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!date || !label.trim()) return;
    setSaving(true);
    try {
      await api.post('/forecasting/holidays', {
        branchId: tenantWide ? undefined : branchId,
        date,
        label: label.trim(),
      });
      toast.success(t('forecasting.holidays.saved'));
      onSaved();
    } catch (e) {
      toast.error(apiError(e, t('forecasting.holidays.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('forecasting.holidays.add')}
      className="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={saving || !date || !label.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('forecasting.holidays.date')} required>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label={t('forecasting.holidays.label')} required>
          <Input
            value={label}
            maxLength={200}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('forecasting.holidays.labelPlaceholder')}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={tenantWide}
            onChange={(e) => setTenantWide(e.target.checked)}
            className="h-4 w-4 rounded border-line-strong"
          />
          {t('forecasting.holidays.tenantWideOption')}
        </label>
      </div>
    </Dialog>
  );
}
